import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import {
  fetchAllUsers,
  MusicLinksResult,
  UserInfo,
} from "../services/AdminService";
import "../Styles/Admin.css";
import { IoExitOutline } from "react-icons/io5";

// COMPONENTS
import { AdminAuthModal } from "../components/admin/AdminAuthModal";
import { MusicLinkFetcher } from "../components/admin/MusicLinkFetcher";
import { CreateTrackForm } from "../components/admin/CreateTrackForm";
import { CreateUserForm } from "../components/admin/CreateUserForm";
import { ArtistImageFetcher } from "../components/admin/ArtistImageFetcher";
import { AdminControlPanel } from "../components/admin/AdminControlPanel";
import { Top100Organizer } from "../components/admin/Top100Organizer";

const AUTHORIZED_ADMIN_ID = import.meta.env.VITE_REACT_APP_ADMIN_UID;

export const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [usersList, setUsersList] = useState<UserInfo[]>([]);
  const [linkResults, setLinkResults] = useState<MusicLinksResult | null>(null);

  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && user.uid === AUTHORIZED_ADMIN_ID) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
      setLoadingAuth(false);
    });

    return () => unsubscribe();
  }, [auth]);

  const loadUsers = useCallback(async () => {
    try {
      const users = await fetchAllUsers();
      setUsersList(users);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadUsers();
    }
  }, [isAuthenticated, loadUsers]);

  const handleExit = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
      navigate("/");
    }
  };

  if (loadingAuth) {
    return (
      <div
        className="admin-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div className="spinner-btn"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-container">
        <AdminAuthModal
          onAuthenticated={() => setIsAuthenticated(true)}
          onExit={handleExit}
        />
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-content">
        <div className="admin-header-row">
          <div className="admin-header-container">
            <h1 className="admin-augio-logo">AUGIO</h1>
            <h1 className="admin-title">Admin</h1>
          </div>
          <button className="btn-secondary btn-exit" onClick={handleExit}>
            <IoExitOutline className="icon" /> Logout
          </button>
        </div>

        <MusicLinkFetcher onLinksFound={setLinkResults} />

        {linkResults && (
          <CreateTrackForm
            linkResults={linkResults}
            usersList={usersList}
            onSuccess={() => setLinkResults(null)}
          />
        )}

        <CreateUserForm onUserCreated={loadUsers} />

        <Top100Organizer />

        <div className="admin-grid">
          <ArtistImageFetcher
            usersList={usersList}
            onImageUpdated={loadUsers}
          />

          <AdminControlPanel />
        </div>
      </div>
    </div>
  );
};
